import { Range, defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DisplayMode } from "~/components/build/Show/lib/useFilterStore";
import EmptyState from "~/components/build/Show/routes/list/-components/EmptyState";
import { TreeItem } from "~/components/build/Show/routes/list/-components/TreeItem";
import { CollapsibleState } from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import { useScrollToId } from "~/components/build/Show/routes/list/-components/useScrollToId";
import { FlatTreeNode } from "~/components/build/Show/routes/list/-components/useTreeNodes";

function stickyAncestorHeight(node: FlatTreeNode): number {
  return node.parentPath.reduce((sum, parent) => sum + parent.height, 0);
}

function isExpanded(node: FlatTreeNode): boolean {
  return (
    node.collapsibleState === CollapsibleState.Expanded ||
    node.collapsibleState === CollapsibleState.Full
  );
}

interface StepTreeProps {
  /**
   * Ref for scrollable element with overflow-scroll
   */
  scrollRef: React.RefObject<HTMLDivElement>;

  /**
   * Item offset when rendering sticky items.
   */
  offsetTop?: number;

  /**
   * Tree display mode
   */
  displayMode: DisplayMode;

  /**
   * Flat list of nodes to render.
   */
  nodes: FlatTreeNode[];
}

/**
 * Render a virtualised tree of states, steps, and jobs with sticky headers
 */
export function StepTree({ nodes, displayMode, scrollRef, offsetTop = 0 }: StepTreeProps) {
  const getStickyTop = useCallback(
    (node: FlatTreeNode) => {
      return offsetTop + stickyAncestorHeight(node);
    },
    [offsetTop],
  );

  const activeStickyIndexRef = useRef<number[]>([]);
  const isActiveSticky = (index: number) => activeStickyIndexRef.current.includes(index);

  const rangeExtractor = useCallback(
    (range: Range) => {
      const currentNode = nodes[range.startIndex];

      activeStickyIndexRef.current = [];

      const hiddenStickyHeight =
        currentNode.parentPath.length > 0
          ? stickyAncestorHeight(currentNode)
          : isExpanded(currentNode)
            ? currentNode.height
            : 0;

      const scrollOffset = (rowVirtualizer.scrollOffset ?? 0) + hiddenStickyHeight;
      const currentOffsetIndex = rowVirtualizer.getVirtualItemForOffset(scrollOffset)?.index ?? 0;
      const currentOffsetNode = nodes[currentOffsetIndex];

      if (currentOffsetNode.depth >= currentNode.depth) {
        if (isExpanded(currentOffsetNode)) {
          activeStickyIndexRef.current.push(currentOffsetNode.index);
        }

        activeStickyIndexRef.current.push(
          ...currentOffsetNode.parentPath.map((path) => path.index),
        );
      } else {
        activeStickyIndexRef.current.push(...currentNode.parentPath.map((path) => path.index));
      }

      const defaultRange = defaultRangeExtractor(range);

      const allIndices = new Set([...defaultRange, ...activeStickyIndexRef.current]);
      return Array.from(allIndices).sort((left, right) => left - right);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activeStickyIndexRef is a ref and rowVirtualizer is stable
    [nodes],
  );

  const maxStickyHeight = useMemo(() => {
    let max = 0;
    for (const node of nodes) {
      const total = stickyAncestorHeight(node) + (node.sticky ? node.height : 0);
      if (total > max) {
        max = total;
      }
    }
    return max;
  }, [nodes]);

  const rowVirtualizer = useVirtualizer({
    count: nodes.length,
    getItemKey: (index) => nodes[index].id,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => nodes[index].height,
    overscan: 50,
    rangeExtractor,
    scrollPaddingStart: maxStickyHeight,
    scrollPaddingEnd: maxStickyHeight,
  });

  // Force the virtualizer to re-detect the scroll element after mount.
  const [, setMounted] = useState(false);
  useLayoutEffect(() => {
    setMounted(true);
  }, []);

  useScrollToId(rowVirtualizer, nodes, activeStickyIndexRef);

  if (nodes.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="p-1 md:p-0">
      <div role="tree" style={{ height: rowVirtualizer.getTotalSize() }} className="relative">
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const node = nodes[virtualItem.index];
          const isSticky = isActiveSticky(virtualItem.index);
          const adjustedDepth = displayMode === DisplayMode.State ? node.depth - 1 : node.depth;

          return (
            <TreeItem
              node={node}
              depth={adjustedDepth}
              isLastChild={node.isLastChild}
              key={virtualItem.key}
              style={{
                position: isSticky ? "sticky" : "absolute",
                top: isSticky ? getStickyTop(node) : 0,
                transform: isSticky ? undefined : `translateY(${virtualItem.start}px)`,
                height: `${virtualItem.size}px`,
                left: 0,
                width: "100%",
                zIndex: node.ariaHidden ? 0 : 20 - node.depth,
              }}
            >
              {node.render({
                ...node,
                depth: adjustedDepth,
              })}
            </TreeItem>
          );
        })}
      </div>
    </div>
  );
}
